import DashboardService from '@/services/dashboard';
import { BoardConfig, buildBoardFilter, groupResultsByColumns, initializeBoardWithStatuses } from '@/config/board-config';
import { DashboardFilter } from '@/interfaces/filters/DashboardFilters';
import { buildColumnStatusMaps, applyHardCodedRulesToStatusList } from '@/utilities/hard-coded-board-rules';
import MODULE from '@/constants/modules';
import GlobalServices from '@/services/global';
import EditDataObject from '@/interfaces/EditDataObject';

/**
 * Board Service - Extends dashboard functionality for board views
 * Leverages existing dashboard API while providing board-specific data transformation
 */
export class BoardService {
  private dashboardService: DashboardService;
  private globalService: GlobalServices;
  private _initializedConfig: BoardConfig | null = null;
  private _statusList: Array<{ id: number; status: string; name: string; }> = [];

  constructor() {
    this.dashboardService = new DashboardService();
    this.globalService = new GlobalServices();
  }

  /**
   * Initialize the board with status data from the API
   * This should be called when the board component loads
   */
  async initializeBoard(boardConfig: BoardConfig, statusList: Array<{ id: number; status?: string; name?: string; }>): Promise<BoardConfig> {
    // Normalize the status list
    this._statusList = statusList.map(status => ({
      id: status.id,
      status: status.status || status.name || '',
      name: status.name || status.status || ''
    }));

    // Initialize the board configuration with status IDs
    this._initializedConfig = initializeBoardWithStatuses(boardConfig, this._statusList);

    // Board initialized with status mappings

    return this._initializedConfig;
  }

  /**
   * Get the initialized board configuration
   * Returns the original config if not yet initialized
   */
  getInitializedConfig(fallback: BoardConfig): BoardConfig {
    return this._initializedConfig || fallback;
  }

  /**
   * Get status assignments for debugging/configuration UI
   */
  getStatusAssignments(): Array<{
    statusId: number;
    statusName: string;
    ruleApplied: string | null;
    columnAssignment: string | null | undefined;
    isExcluded: boolean;
  }> {
    if (this._statusList.length === 0) {
      return [];
    }

    return applyHardCodedRulesToStatusList(this._statusList);
  }

  /**
   * Get status IDs for a specific column
   */
  getStatusIdsForColumn(columnId: string): number[] {
    if (!this._initializedConfig) {
      return [];
    }

    const column = this._initializedConfig.columns.find(col => col.id === columnId);
    return column ? column.statusIds : [];
  }

  /**
   * Get all excluded status IDs (for filtering API calls)
   */
  getExcludedStatusIds(): number[] {
    const assignments = this.getStatusAssignments();
    return assignments
      .filter(assignment => assignment.isExcluded)
      .map(assignment => assignment.statusId);
  }

  /**
   * Get column status mappings for configuration display
   */
  getColumnStatusMappings(): { [columnId: string]: { statusIds: number[]; statusNames: string[] } } {
    if (!this._initializedConfig) {
      return {};
    }

    const mappings: { [columnId: string]: { statusIds: number[]; statusNames: string[] } } = {};

    this._initializedConfig.columns.forEach(column => {
      const statusNames = column.statusIds.map(statusId => {
        const status = this._statusList.find(s => s.id === statusId);
        return status ? status.status : `Status ID ${statusId}`;
      });

      mappings[column.id] = {
        statusIds: column.statusIds,
        statusNames
      };
    });

    return mappings;
  }

  /**
   * Load board data using the existing dashboard API
   * Applies base filters and groups results by board columns
   */
  async loadBoardData(boardConfig: BoardConfig, additionalFilters?: any, forceRefresh: boolean = false): Promise<any> {
    try {
      // Use the initialized config if available
      const activeConfig = this.getInitializedConfig(boardConfig);

      // Build the filter using base configuration and any additional filters
      // Now uses async version with invoice status filtering
      const filter = await buildBoardFilter(
        activeConfig,
        additionalFilters,
        this.globalService,
        MODULE,
        forceRefresh
      ) as DashboardFilter;
      
      // Use the existing dashboard service to get projects
      const response = await this.dashboardService.getProjects(filter);
      
      if (!response || !response.resources) {
        throw new Error('Invalid response from dashboard API');
      }

      // Group the results by board columns based on project status ID
      const groupedResults = groupResultsByColumns(response.resources, activeConfig);
      
      // Transform to board column format
      const boardColumns = activeConfig.columns.map(columnConfig => ({
        id: columnConfig.id,
        title: columnConfig.title,
        subTitle: columnConfig.subTitle || '',
        borderClass: columnConfig.borderClass,
        list: groupedResults[columnConfig.id].map(item => this.transformProjectToCard(item, activeConfig)),
        order: columnConfig.order
      }));

      return {
        columns: boardColumns,
        total: response.total,
        success: response.success
      };
    } catch (error) {
      // Error loading board data - re-thrown for handling by caller
      throw error;
    }
  }

  /**
   * Transform a project item from the dashboard API to a board card format
   */
  private transformProjectToCard(item: any, boardConfig: BoardConfig): any {
    const cardFields = boardConfig.cardFields;
    
    return {
      id: item[cardFields.primary] || item.projectRefNumber,
      intProjectID: item.intProjectID,
      client: item[cardFields.customer] || item.businessName,
      location: item[cardFields.location] || item.location,
      total: item[cardFields.amount] || '0.00',
      modifiedDate: new Date(item.utcCreatedDate || Date.now()),
      status: item[cardFields.status] || item.status,
      technicianStatus: item.technicianStatus,
      type: item[cardFields.category] || item.type,
      assignee: item[cardFields.assignee] || 'Unassigned',
      priority: item[cardFields.priority] || 'Medium',
      dueDate: item[cardFields.due_date] || item.dueDate,
      displayDueDate: item.displayDueDate,
      displayDueDateStatus: item.displayDueDateStatus,
      createdBy: item.createdBy,
      primaryContactName: item.primaryContactName,
      primaryContactPhone: item.primaryContactPhone,
      htmlViewStatus: item.htmlViewStatus,
      htmlColourStatus: item.htmlColourStatus,
      htmlColourTechnicianStatus: item.htmlColourTechnicianStatus,
      htmlViewDueDate: item.htmlViewDueDate,
      viewDetailsUrl: item.viewDetailsUrl,
      // Determine badge and border classes based on status
      badgeStatusClass: this.getStatusBadgeClass(item.status),
      borderClass: this.getStatusBorderClass(item.status),
      // Additional fields that might be useful
      projectName: item.projectName,
      jobDescription: item.jobDescription,
      displayCreatedDate: item.displayCreatedDate,
      time: item.time
    };
  }

  /**
   * Get badge class based on status
   */
  private getStatusBadgeClass(status: string): string {
    const statusClassMap: { [key: string]: string } = {
      'New Service Call': 'orange',
      'New': 'orange',
      'Scheduled': 'blue',
      'Dispatched': 'purple',
      'In Progress': 'yellow',
      'On Hold': 'grey',
      'Waiting': 'grey',
      'Completed': 'green',
      'Completed - To Be Invoiced': 'teal',
      'Invoice - Created': 'success'
    };
    
    return statusClassMap[status] || 'secondary';
  }

  /**
   * Get border class based on status
   */
  private getStatusBorderClass(status: string): string {
    const statusBorderMap: { [key: string]: string } = {
      'New Service Call': 'border-left-orange',
      'New': 'border-left-orange',
      'Scheduled': 'border-left-blue',
      'Dispatched': 'border-left-purple',
      'In Progress': 'border-left-yellow',
      'On Hold': 'border-left-grey',
      'Waiting': 'border-left-grey',
      'Completed': 'border-left-green',
      'Completed - To Be Invoiced': 'border-left-teal',
      'Invoice - Created': 'border-left-success'
    };
    
    return statusBorderMap[status] || 'border-left-secondary';
  }

  /**
   * Update job status (for drag and drop operations)
   * This would need to be implemented to call the appropriate API endpoint
   */
  async updateJobStatus(jobId: string, newStatus: string): Promise<any> {
    try {
      // TODO: Implement status update API call
      // This might involve calling a projects update endpoint
      // updateJobStatus not yet implemented - returning success for UI testing
      
      // For now, return success to allow UI testing
      return { success: true };
    } catch (error) {
      // Error updating job status - re-thrown for handling by caller
      throw error;
    }
  }

  /**
   * Update job status by status ID (preferred method for drag and drop operations)
   * Uses the existing global/update API infrastructure
   */
  async updateJobStatusById(jobId: string, newStatusId: number, job: any): Promise<any> {
    try {
      // Use the existing global services infrastructure for status updates
      const editObj = {
        id: job.intProjectID,
        referenceNumber: job.id, // This is the project reference number (e.g., "WO5208")
        propertyName: "IntProjectStatusID",
        propertyValue: newStatusId.toString(), // Convert to string as required by EditDataObject
        displayName: "Status/Priority",
        displayValue: this.getStatusDisplayValue(newStatusId),
        previousDisplayValue: job.status,
        moduleId: MODULE.PROJECTS.ModuleId, // 2
        actionId: MODULE.PROJECTS.ActionId, // 23
      } as Partial<EditDataObject>;

      // Use global services to perform the update (postData will add the missing auth fields)
      const response = await this.globalService.postData(editObj as EditDataObject);
      
      if (response && response.success) {
        return response;
      } else {
        throw new Error('Failed to update job status');
      }
    } catch (error) {
      // Error updating job status by ID - re-thrown for handling by caller
      throw error;
    }
  }

  /**
   * Get display value for a status ID
   */
  private getStatusDisplayValue(statusId: number): string {
    const status = this._statusList.find(s => s.id === statusId);
    return status ? status.status : `Status ID ${statusId}`;
  }

  /**
   * Apply additional filters to the board
   * Maintains the base filter while adding user-specific filters
   */
  async applyFilters(boardConfig: BoardConfig, filters: any): Promise<any> {
    return this.loadBoardData(boardConfig, filters);
  }

  /**
   * Reset filters to base configuration
   */
  async resetFilters(boardConfig: BoardConfig): Promise<any> {
    return this.loadBoardData(boardConfig);
  }

  /**
   * Search within board data
   */
  async searchBoard(boardConfig: BoardConfig, searchTerm: string, additionalFilters?: any): Promise<any> {
    const searchFilters = {
      ...additionalFilters,
      // Add search parameters that the dashboard API supports
      projectRefNumber: searchTerm.includes('WO') ? searchTerm : undefined,
      keywordAddress: !searchTerm.includes('WO') ? searchTerm : undefined
    };

    return this.loadBoardData(boardConfig, searchFilters);
  }
}

export default BoardService;
